// components/notifications/meeting/PollDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  Avatar,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as MaybeIcon,
  Close as CloseIcon,
  EventAvailable as EventIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { axiosService } from 'shared/helpers/axios';
// import { getUser } from 'shared/hooks/user.actions';
import { useUser } from "shared/helpers/axios";

function PollDialog({ open, onClose, meetingRequestId, pollId, onVoteSubmit }) {
  const [loading, setLoading] = useState(false);
  const [pollData, setPollData] = useState(null);
  const [meetingRequest, setMeetingRequest] = useState(null);
  const [participantsData, setParticipantsData] = useState({});
  const [selectedVotes, setSelectedVotes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [allVotes, setAllVotes] = useState([]); // Store all votes with user info
  useUser();

  useEffect(() => {
    if (open && (meetingRequestId || pollId)) {
      fetchPollAndInvitees();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meetingRequestId, pollId]);

  const fetchPollAndInvitees = async () => {
    setLoading(true);
    try {
      let poll = null;
      let meetingRequestData = null;

      // If pollId is provided, fetch that specific poll
      if (pollId) {
        const detailedPollResponse = await axiosService.get(`/meeting/meeting-polls/${pollId}/`);
        poll = detailedPollResponse.data;
        
        // Fetch the associated meeting request
        if (poll.meeting_request) {
          const mrResponse = await axiosService.get(`/meeting/meeting-requests/${poll.meeting_request}/`);
          meetingRequestData = mrResponse.data;
        }
      } 
      // Otherwise, fetch by meeting request ID and get the most recent poll
      else if (meetingRequestId) {
        // Fetch meeting request
        const mrResponse = await axiosService.get(`/meeting/meeting-requests/${meetingRequestId}/`);
        meetingRequestData = mrResponse.data;

        // Fetch all polls for this meeting request, ordered by created date
        const pollResponse = await axiosService.get(`/meeting/meeting-polls/?meeting_request=${meetingRequestId}&ordering=-created`);

        // Handle both paginated and non-paginated responses
        const polls = pollResponse.data.results || pollResponse.data;
        
        if (polls && polls.length > 0) {
          // Get the most recent poll (first one due to ordering=-created)
          const pollId = polls[0].id;
          
          // Fetch detailed poll data with votes
          const detailedPollResponse = await axiosService.get(`/meeting/meeting-polls/${pollId}/`);
          poll = detailedPollResponse.data;
        }
      }

      if (!poll) {
        setLoading(false);
        return;
      }

      setMeetingRequest(meetingRequestData);
      setPollData(poll);

      // Initialize selectedVotes with current user's votes
      if (poll.my_votes) {
        setSelectedVotes(poll.my_votes);
      }

      // Fetch all votes for the poll
      try {
        const votesResponse = await axiosService.get(`/meeting/meeting-polls/${poll.id}/votes/`);
        console.log('Votes response:', votesResponse.data);
        setAllVotes(votesResponse.data || []);
      } catch (error) {
        console.error('Error fetching votes:', error);
      }

      // Fetch invitees data (including the creator)
      const allUserIds = new Set();
      
      // Add invitees
      if (meetingRequestData?.invitees && meetingRequestData.invitees.length > 0) {
        meetingRequestData.invitees.forEach(invitee => allUserIds.add(String(invitee.id)));
      }
      
      // Add creator
      if (meetingRequestData?.created_by) {
        allUserIds.add(String(meetingRequestData.created_by.id));
      }

      if (allUserIds.size > 0) {
        const participantsMap = {};
        await Promise.all(
          Array.from(allUserIds).map(async (inviteeId) => {
            try {
              const userResponse = await axiosService.get(`/users/${inviteeId}/`);
              participantsMap[inviteeId] = userResponse.data || userResponse.data.results;
            } catch (err) {
              console.error(`Failed to fetch user ${inviteeId}:`, err);
            }
          })
        );
        
        setParticipantsData(participantsMap);
      }
    } catch (error) {
      console.error('Error fetching poll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVoteChange = (optionId, availability) => {
    setSelectedVotes(prev => ({
      ...prev,
      [optionId]: availability
    }));
  };

  const handleSubmitVotes = async () => {
    if (!pollData || Object.keys(selectedVotes).length === 0) return;

    setSubmitting(true);
    try {
      await axiosService.post(`/meeting/meeting-polls/${pollData.id}/vote/`, {
        votes: selectedVotes
      });

      // Refresh poll data
      await fetchPollAndInvitees();
      
      if (onVoteSubmit) {
        onVoteSubmit();
      }
    } catch (error) {
      console.error('Error submitting votes:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getVotesForOption = (optionId) => {
    const option = pollData?.options?.find(opt => opt.id === optionId);
    if (!option) return { yes: 0, ifneedbe: 0, no: 0 };
    return {
      yes: option.yes || 0,
      ifneedbe: option.ifneedbe || 0,
      no: option.no || 0
    };
  };

  // Get voter names for a specific option and availability
  const getVotersForOptionAndAvailability = (optionId, availability) => {
    if (!allVotes || allVotes.length === 0) return [];
    
    return allVotes
      .filter(vote => String(vote.option_id) === String(optionId) && vote.availability === availability)
      .map(vote => vote.user_name || 'Unknown')
      .filter(name => name !== 'Unknown');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <EventIcon color="primary" />
            <Typography variant="h6">Meeting Time Poll</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close poll dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : pollData && meetingRequest ? (
          <Stack spacing={3}>
            {/* Meeting Info */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Meeting: {meetingRequest.title}
              </Typography>
              {meetingRequest.description && (
                <Typography variant="body2" color="text.secondary">
                  {meetingRequest.description}
                </Typography>
              )}
            </Paper>

            {/* Participants Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Participants ({Object.keys(participantsData).length})
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.values(participantsData).map((participant) => (
                  <Chip
                    key={participant.id}
                    avatar={
                    

                    participant.avatar ? (
                  <Avatar
                    src={participant.avatar}
                    alt={participant.name || participant.username || 'Participant'}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : (
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }} alt={participant.name || participant.username || 'Participant'}>
                    {participant?.name?.charAt(0) || participant?.username?.charAt(0) || 'U'}
                  </Avatar>
                )}
                    
                    label={participant.name || participant.email}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>

            <Divider />

            {/* Poll Options */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Select Your Availability
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Vote for your availability on each time slot
              </Typography>

              <Stack spacing={2} sx={{ mt: 2 }}>
                {pollData.options?.map((option) => {
                  const votes = getVotesForOption(option.id);
                  const userVote = selectedVotes[option.id];

                  return (
                    <Paper
                      key={option.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        border: userVote ? 1 : 1,
                        borderColor: userVote ? 'primary.main' : 'primary.light',
                        // bgcolor: userVote ? 'action.selected' : 'background.paper',
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                     

<Grid item xs={12} md={6}>
  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
    <Typography variant="body1" fontWeight={500}>
      {format(new Date(option.start), 'PPP')}
    </Typography>
    <Chip
      label={`${format(new Date(option.start), 'p')} – ${format(new Date(option.end), 'p')}`}
      color="primary"
      variant="outlined"
      size="small"
      sx={{ fontWeight: 500 }}
    />
  </Box>
</Grid>




                        <Grid item xs={12} md={6}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">

                             <Tooltip 
                                title={getVotersForOptionAndAvailability(option.id, 'yes').join(', ') || 'No voters yet'}
                                arrow
                              >



                            <Button
                              variant={userVote === 'yes' ? 'contained' : 'outlined'}
                              color="success"
                              size="small"
                              startIcon={<CheckIcon />}
                              onClick={() => handleVoteChange(option.id, 'yes')}
                            >
                              Yes ({votes.yes})
                                </Button>    
                        </Tooltip>
                        
                        
                        <Tooltip 
                                title={getVotersForOptionAndAvailability(option.id, 'ifneedbe').join(', ') || 'No voters yet'}
                                arrow
                              >

                              
                            <Button
                              variant={userVote === 'ifneedbe' ? 'contained' : 'outlined'}
                              color="warning"
                              size="small"
                              startIcon={<MaybeIcon />}
                              onClick={() => handleVoteChange(option.id, 'ifneedbe')}
                            >
                              Maybe ({votes.ifneedbe})
                            </Button>
                          </Tooltip>          

                            <Tooltip 
                                title={getVotersForOptionAndAvailability(option.id, 'no').join(', ') || 'No voters yet'}
                                arrow
                              >
                            <Button
                              variant={userVote === 'no' ? 'contained' : 'outlined'}
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => handleVoteChange(option.id, 'no')}
                            >
                              No ({votes.no})
                            </Button>
                          </Tooltip>

                          </Stack>
                        </Grid>
                      </Grid>

                      {/* Show vote summary with voter names */}
           
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No poll available for this meeting request.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Close
        </Button>
        {pollData && Object.keys(selectedVotes).length > 0 && (
          <Button
            variant="contained"
            onClick={handleSubmitVotes}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Votes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default PollDialog;
